import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import PixiStage from '../shopfloor/pixi/PixiStage';
import Toolbar from '../shopfloor/ui/Toolbar';
import Palette from '../shopfloor/ui/Palette';
import Inspector from '../shopfloor/ui/Inspector';
import { useShopStore } from '../shopfloor/store/useShopStore';
import { getAssets } from '../shopfloor/ui/Palette';

// Use simpler path - Vite proxy will rewrite to /machinesim/socket.io
const SOCKET_PATH = '/socket.io';

// Background color presets arranged in a circle
const BG_COLORS = [
  { name: 'Dark Navy', value: '#1a1a2e' },
  { name: 'Charcoal', value: '#1f2937' },
  { name: 'Slate', value: '#0f172a' },
  { name: 'Dark Gray', value: '#171717' },
  { name: 'Midnight', value: '#0c0a09' },
  { name: 'Deep Blue', value: '#172554' },
  { name: 'Forest', value: '#14532d' },
  { name: 'Wine', value: '#450a0a' },
];

// Grid color presets
const GRID_PRESETS = [
  { name: 'Subtle', color: 0x3a3a3a, opacity: 0.3 },
  { name: 'Medium', color: 0x4a4a4a, opacity: 0.5 },
  { name: 'Bright', color: 0x5a5a5a, opacity: 0.7 },
  { name: 'White', color: 0xffffff, opacity: 0.15 },
  { name: 'Blue', color: 0x3b82f6, opacity: 0.3 },
  { name: 'Cyan', color: 0x22d3ee, opacity: 0.25 },
];

// Floor size presets
const FLOOR_PRESETS = [
  { name: 'Small Shop', size: 20, desc: '20×20' },
  { name: 'Medium Shop', size: 30, desc: '30×30' },
  { name: 'Big Floor', size: 40, desc: '40×40' },
];

// Circular color picker component
function ColorWheel({ isOpen, currentColor, onSelect, onClose, onCustom }) {
  const radius = 50;
  
  return (
    <div className={`absolute bottom-0 right-0 transition-all duration-300 ease-out ${
      isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
    }`}>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0" onClick={onClose} />}
      
      {/* Color wheel */}
      <div className="relative w-32 h-32">
        {BG_COLORS.map((color, i) => {
          const angle = (i * 360 / BG_COLORS.length - 90) * (Math.PI / 180);
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          
          return (
            <button
              key={color.value}
              onClick={() => onSelect(color.value)}
              className={`absolute w-7 h-7 rounded-full border-2 transition-all duration-200 hover:scale-125 shadow-lg ${
                currentColor === color.value 
                  ? 'border-white scale-110' 
                  : 'border-gray-600 hover:border-gray-400'
              }`}
              style={{ 
                backgroundColor: color.value,
                left: `calc(50% + ${x}px - 14px)`,
                top: `calc(50% + ${y}px - 14px)`,
              }}
              title={color.name}
            />
          );
        })}
        
        {/* Center - custom color picker */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <label 
            className="w-10 h-10 rounded-full bg-gray-700 border-2 border-gray-500 hover:border-gray-300 cursor-pointer flex items-center justify-center shadow-lg transition-colors"
            title="Custom color"
          >
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            <input
              type="color"
              value={currentColor}
              onChange={(e) => onSelect(e.target.value)}
              className="sr-only"
            />
          </label>
        </div>
      </div>
    </div>
  );
}

export default function ShopFloorPage({ companySlug = null } = {}) {
  const { 
    setManifest, 
    setAllMachineStatus, 
    loadFromStorage,
    layout,
    currentTool,
    setTool,
    setGridSize,
    enableCustomFloor,
    disableCustomFloor,
    fillFloorTiles,
    clearFloorTiles,
  } = useShopStore();
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(true); // Start collapsed
  const [bgColor, setBgColor] = useState(() => localStorage.getItem('shopfloor-bg') || '#1a1a2e');
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [gridColor, setGridColor] = useState(() => parseInt(localStorage.getItem('shopfloor-grid-color') || '0x4a4a4a'));
  const [gridOpacity, setGridOpacity] = useState(() => parseFloat(localStorage.getItem('shopfloor-grid-opacity') || '0.5'));
  const [showGridPicker, setShowGridPicker] = useState(false);
  const [showFloorPicker, setShowFloorPicker] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);

  const isCustomFloorMode = layout.floorTiles !== null;
  const isDrawing = currentTool === 'floor-draw' || currentTool === 'floor-erase';
  const isEmptyFloor = (layout.entities?.length ?? 0) === 0;

  // Esc exits the floor drawing tool (a mode with the panel dismissed — needs an escape hatch).
  useEffect(() => {
    if (!isDrawing) return;
    const onKey = (e) => { if (e.key === 'Escape') setTool('select'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDrawing, setTool]);

  // Initialize with assets and load saved layout
  useEffect(() => {
    setManifest({ assets: getAssets() });
    loadFromStorage();
  }, []);

  // Save preferences
  useEffect(() => {
    localStorage.setItem('shopfloor-bg', bgColor);
  }, [bgColor]);

  useEffect(() => {
    localStorage.setItem('shopfloor-grid-color', gridColor.toString());
    localStorage.setItem('shopfloor-grid-opacity', gridOpacity.toString());
  }, [gridColor, gridOpacity]);

  // Connect to WebSocket for machine status updates (only when this page is mounted)
  useEffect(() => {
    const socket = io({
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
    });

    // Machine updates are sent per company room, so say which company this floor is.
    socket.on('connect', () => {
      setSocketConnected(true);
      if (companySlug) socket.emit('joinCompany', companySlug);
    });
    socket.on('disconnect', () => setSocketConnected(false));

    socket.on('allMachinesUpdate', (machines) => {
      const statusMap = {};
      machines.forEach((m) => {
        statusMap[m.name] = m.state;
      });
      setAllMachineStatus(statusMap);
    });

    socket.on('machineUpdate', (data) => {
      useShopStore.getState().updateMachineStatus(data.name, data.state);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [companySlug]);

  return (
    <div className="h-screen flex flex-col bg-gray-900 text-white overflow-hidden">
      {/* Toolbar */}
      <Toolbar />

      {/* Main Content - Canvas takes full space */}
      <div className="flex-1 relative overflow-hidden" style={{ backgroundColor: bgColor }}>
        {/* Pixi Stage - full area */}
        <PixiStage bgColor={bgColor} gridColor={gridColor} gridOpacity={gridOpacity} />

        {/* First-run prompt when the floor is empty — an empty canvas otherwise gives no
            cue that this is an editor or how to place a machine (Peak-End / Cognitive Load). */}
        {isEmptyFloor && !isDrawing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[15]">
            <div className="bg-gray-800/70 text-gray-300 text-sm rounded-lg px-5 py-3 shadow-lg text-center">
              <div className="font-medium text-white mb-1">Empty floor</div>
              Open the palette on the left and drag a machine onto the grid to begin.
            </div>
          </div>
        )}

        {/* Persistent drawing-mode banner (the floor picker closes after "Start Drawing",
            leaving a modal state with no on-canvas cue) — Doherty / Flow. */}
        {isDrawing && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 bg-green-600/90 text-white text-xs rounded-full px-3 py-1 shadow-lg flex items-center gap-2">
            <span>{currentTool === 'floor-erase' ? 'Erasing floor tiles' : 'Drawing floor tiles'} — click tiles</span>
            <span className="opacity-80">· Esc to finish</span>
          </div>
        )}

        {/* Live-data status: distinguishes a working feed from a silent/stale one (Doherty). */}
        <div className="absolute top-3 right-3 z-30 flex items-center gap-1.5 text-xs bg-gray-800/70 rounded px-2 py-1"
          title={socketConnected ? 'Live machine feed connected' : 'Machine feed disconnected'}>
          <span className={`w-2 h-2 rounded-full ${socketConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className={socketConnected ? 'text-gray-300' : 'text-gray-400'}>{socketConnected ? 'Live' : 'Offline'}</span>
        </div>

        {/* Left Sidebar - Asset Palette (overlays) */}
        <div className="absolute left-0 top-0 h-full z-10">
          <Palette 
            collapsed={paletteCollapsed} 
            onToggle={() => setPaletteCollapsed(!paletteCollapsed)} 
          />
        </div>

        {/* Right Sidebar - Inspector (overlays) */}
        <div className="absolute right-0 top-0 h-full z-10">
          <Inspector 
            collapsed={inspectorCollapsed} 
            onToggle={() => setInspectorCollapsed(!inspectorCollapsed)} 
          />
        </div>
        
        {/* Hover navigation - left edge */}
        <Link 
          to="/" 
          className="absolute left-14 top-1/2 -translate-y-1/2 p-2 bg-gray-800/50 hover:bg-gray-800 rounded text-gray-500 hover:text-white opacity-40 hover:opacity-100 transition-all z-30"
          title="Simulator"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        
        {/* Hover navigation - right edge */}
        <Link 
          to="/erp" 
          className="absolute right-14 top-1/2 -translate-y-1/2 p-2 bg-gray-800/50 hover:bg-gray-800 rounded text-gray-500 hover:text-white opacity-40 hover:opacity-100 transition-all z-30"
          title="ERP"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
        
        {/* Floating controls - bottom right */}
        <div className="absolute bottom-4 right-4 z-20 flex items-end gap-3">
          {/* Floor settings */}
          <div className="relative">
            <button
              onClick={() => { setShowFloorPicker(!showFloorPicker); setShowGridPicker(false); setShowBgPicker(false); }}
              className={`w-10 h-10 rounded-full border-2 shadow-lg transition-all duration-200 flex items-center justify-center ${
                showFloorPicker || isCustomFloorMode
                  ? 'border-indigo-400 scale-110 bg-indigo-900/50' 
                  : 'border-gray-600 hover:border-gray-400 hover:scale-105 bg-gray-800'
              }`}
              title="Floor settings"
            >
              <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </button>
            
            {showFloorPicker && (
              <>
                <div className="fixed inset-0" onClick={() => setShowFloorPicker(false)} />
                <div className="absolute bottom-12 right-0 bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-xl w-[220px]">
                  <div className="text-xs text-gray-400 mb-3 font-medium">Floor Size</div>
                  <div className="flex flex-col gap-2 mb-4">
                    {FLOOR_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => {
                          setGridSize(preset.size);
                          if (isCustomFloorMode) {
                            fillFloorTiles();
                          }
                        }}
                        className={`px-3 py-2 text-xs rounded transition-colors flex items-center justify-between ${
                          layout.gridSize === preset.size
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        <span>{preset.name}</span>
                        <span className="text-gray-400">{preset.desc}</span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-700 pt-3">
                    <div className="text-xs text-gray-400 mb-2 font-medium">Custom Floor Shape</div>
                    {!isCustomFloorMode ? (
                      <button
                        onClick={() => {
                          enableCustomFloor();
                          setTool('floor-draw');
                          setShowFloorPicker(false);
                        }}
                        className="w-full px-3 py-2 text-xs bg-gray-700 text-gray-300 hover:bg-gray-600 rounded flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Start Drawing Floor
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => setTool('floor-draw')}
                            className={`flex-1 px-2 py-2 text-xs rounded flex items-center justify-center gap-1 transition-colors ${
                              currentTool === 'floor-draw'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                            title="Draw floor tiles"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Draw
                          </button>
                          <button
                            onClick={() => setTool('floor-erase')}
                            className={`flex-1 px-2 py-2 text-xs rounded flex items-center justify-center gap-1 transition-colors ${
                              currentTool === 'floor-erase'
                                ? 'bg-red-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                            title="Erase floor tiles"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                            Erase
                          </button>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={fillFloorTiles}
                            className="flex-1 px-2 py-1.5 text-xs bg-gray-700 text-gray-300 hover:bg-gray-600 rounded"
                          >
                            Fill All
                          </button>
                          <button
                            onClick={clearFloorTiles}
                            className="flex-1 px-2 py-1.5 text-xs bg-gray-700 text-gray-300 hover:bg-gray-600 rounded"
                          >
                            Clear All
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            disableCustomFloor();
                            setTool('select');
                          }}
                          className="w-full px-2 py-1.5 text-xs bg-gray-600 text-gray-200 hover:bg-gray-500 rounded"
                        >
                          Done Editing
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Grid picker */}
          <div className="relative">
            <button
              onClick={() => { setShowGridPicker(!showGridPicker); setShowBgPicker(false); setShowFloorPicker(false); }}
              className={`w-10 h-10 rounded-full border-2 shadow-lg transition-all duration-200 flex items-center justify-center ${
                showGridPicker 
                  ? 'border-white scale-110 bg-gray-700' 
                  : 'border-gray-600 hover:border-gray-400 hover:scale-105 bg-gray-800'
              }`}
              title="Grid settings"
            >
              <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            
            {showGridPicker && (
              <>
                <div className="fixed inset-0" onClick={() => setShowGridPicker(false)} />
                <div className="absolute bottom-12 right-0 bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-xl w-[200px]">
                  <div className="text-xs text-gray-400 mb-3 font-medium">Grid Style</div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {GRID_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => {
                          setGridColor(preset.color);
                          setGridOpacity(preset.opacity);
                        }}
                        className={`px-2 py-2 text-xs rounded transition-colors ${
                          gridColor === preset.color && gridOpacity === preset.opacity
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-gray-700 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">Opacity</span>
                      <span className="text-xs text-gray-500">{Math.round(gridOpacity * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={gridOpacity}
                      onChange={(e) => setGridOpacity(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:hover:bg-indigo-400"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Background color picker */}
          <div className="relative">
            <button
              onClick={() => { setShowBgPicker(!showBgPicker); setShowGridPicker(false); setShowFloorPicker(false); }}
              className={`w-10 h-10 rounded-full border-2 shadow-lg transition-all duration-200 ${
                showBgPicker 
                  ? 'border-white scale-110' 
                  : 'border-gray-600 hover:border-gray-400 hover:scale-105'
              }`}
              style={{ backgroundColor: bgColor }}
              title="Change background"
            />
            
            {/* Circular color wheel */}
            <ColorWheel
              isOpen={showBgPicker}
              currentColor={bgColor}
              onSelect={(color) => {
                setBgColor(color);
                setShowBgPicker(false);
              }}
              onClose={() => setShowBgPicker(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
